import React, { useState, useEffect, useCallback } from 'react';
import { Patient } from '../../../types/patient';
import { formatDate } from '../utils/patientUtils';
import { apiCall } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import AddDiagnosisModal from '../../modals/AddDiagnosisModal';
import AddAllergyModal from '../../modals/AddAllergyModal';
import AddMedicationModal, { Medication } from '../../modals/AddMedicationModal';

interface OverviewTabProps {
  patient: Patient;
}

interface PatientDiagnosis {
  id: string;
  diagnosis_name?: string;
  diagnosis_icd_10?: string;
  diagnosis_date?: string;
  severity?: string;
  is_primary?: boolean;
}

interface PatientAllergy {
  id: string;
  allergy_name: string;
  reaction?: string;
  severity: string;
  is_verified?: boolean;
}

interface RecentNote {
  id: string;
  subject?: string;
  note_type?: string;
  content?: string;
  note_date: string;
  provider_name?: string;
}

interface ClinicalSummary {
  diagnoses: PatientDiagnosis[];
  allergies: PatientAllergy[];
  medications: Medication[];
  recent_notes: RecentNote[];
}

/**
 * OverviewTab component displays a dashboard of key patient information
 */
const OverviewTab: React.FC<OverviewTabProps> = ({ patient }) => {
  const { token } = useAuth();
  const [isDiagnosisModalOpen, setIsDiagnosisModalOpen] = useState(false);
  const [isAllergyModalOpen, setIsAllergyModalOpen] = useState(false);
  const [isMedicationModalOpen, setIsMedicationModalOpen] = useState(false);
  const [discontinuingMedication, setDiscontinuingMedication] = useState<Medication | null>(null);
  const [showDiscontinuedMeds, setShowDiscontinuedMeds] = useState(false);
  const [summary, setSummary] = useState<ClinicalSummary>({ diagnoses: [], allergies: [], medications: [], recent_notes: [] });
  const [loading, setLoading] = useState(true);

  const fetchPatientClinicalData = useCallback(async () => {
    if (!patient?.id || !token) return;
    setLoading(true);
    try {
      const response = await apiCall(
        `/clinical/patients/${patient.id}/clinical-summary/?include_discontinued=${showDiscontinuedMeds}`,
        { method: 'GET' },
        token
      );

      if (response.ok) {
        const data = await response.json();
        setSummary({
          diagnoses: data.diagnoses || [],
          allergies: data.allergies || [],
          medications: data.medications || [],
          recent_notes: data.recent_notes || [],
        });
      }
    } catch (error) {
      console.error('Error fetching clinical data:', error);
    } finally {
      setLoading(false);
    }
  }, [patient?.id, token, showDiscontinuedMeds]);

  useEffect(() => {
    fetchPatientClinicalData();
  }, [fetchPatientClinicalData]);

  const handleDiagnosisAdded = () => {
    fetchPatientClinicalData();
  };

  const handleAllergyAdded = () => {
    fetchPatientClinicalData();
  };

  const handleMedicationAdded = () => {
    fetchPatientClinicalData();
  };

  const activeMedications = summary.medications.filter(m => m.status !== 'Discontinued');
  const discontinuedMedications = summary.medications.filter(m => m.status === 'Discontinued');

  return (
    <div className="space-y-6">
      {/* Clinical Information Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

      {/* Active Diagnoses */}
      <div className="bg-base-100 shadow rounded-lg p-6 border border-accent">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-base-content opacity-900">Active Diagnoses</h3>
          <button
            onClick={() => setIsDiagnosisModalOpen(true)}
            className="text-sm text-secondary hover:text-secondary-focus"
          >
            Add
          </button>
        </div>

        <div className="space-y-4">
          {loading ? (
            <p className="text-base-content opacity-500 text-sm">Loading...</p>
          ) : summary.diagnoses.length > 0 ? (
            summary.diagnoses.map((diagnosis) => (
              <div key={diagnosis.id} className="border-l-4 border-blue-500 pl-3">
                <p className="font-medium">{diagnosis.diagnosis_name}</p>
                <p className="text-sm text-base-content opacity-500">
                  {diagnosis.diagnosis_icd_10 && `ICD-10: ${diagnosis.diagnosis_icd_10} | `}
                  {diagnosis.diagnosis_date && `Diagnosed: ${formatDate(diagnosis.diagnosis_date)}`}
                  {diagnosis.severity && ` | Severity: ${diagnosis.severity}`}
                </p>
                {diagnosis.is_primary && (
                  <span className="inline-block mt-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                    Primary
                  </span>
                )}
              </div>
            ))
          ) : (
            <p className="text-base-content opacity-500 text-sm">No active diagnoses recorded</p>
          )}
        </div>
      </div>

      {/* Allergies */}
      <div className="bg-base-100 shadow rounded-lg p-6 border border-accent">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-base-content opacity-900">Allergies</h3>
          <button
            onClick={() => setIsAllergyModalOpen(true)}
            className="text-sm text-secondary hover:text-secondary-focus"
          >
            Add
          </button>
        </div>

        <div className="space-y-4">
          {loading ? (
            <p className="text-base-content opacity-500 text-sm">Loading...</p>
          ) : summary.allergies.length > 0 ? (
            summary.allergies.map((allergy) => (
              <div key={allergy.id} className="border-l-4 border-red-500 pl-3">
                <p className="font-medium">{allergy.allergy_name}</p>
                <p className="text-sm text-base-content opacity-500">
                  {allergy.reaction} | Severity: {allergy.severity}
                </p>
                {allergy.is_verified && (
                  <span className="inline-block mt-1 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                    Verified
                  </span>
                )}
              </div>
            ))
          ) : (
            <p className="text-base-content opacity-500 text-sm">No allergies recorded</p>
          )}
        </div>
      </div>

      {/* Upcoming Appointments/Procedures */}
      <div className="bg-base-100 shadow rounded-lg p-6 border border-accent">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-base-content opacity-900">Upcoming Appointments/Procedures</h3>
          <button className="text-sm text-secondary hover:text-secondary-focus">
            Schedule
          </button>
        </div>

        <div className="space-y-4">
          {patient.appointments && patient.appointments.length > 0 ? (
            patient.appointments
              .filter(appointment => new Date(appointment.start_time) > new Date())
              .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
              .slice(0, 3)
              .map((appointment, index) => (
                <div key={appointment.id || index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium">{appointment.appointment_type || 'Consultation'}</p>
                    <p className="text-sm text-base-content opacity-500">
                      {formatDate(appointment.start_time)} | {new Date(appointment.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </p>
                    <p className="text-sm text-base-content opacity-500">
                      Provider: {appointment.provider_name || 'Not assigned'}
                    </p>
                  </div>
                </div>
              ))
          ) : (
            <p className="text-base-content opacity-500 text-sm">No upcoming appointments/procedures</p>
          )}
        </div>
      </div>

      {/* Recent Clinical Notes */}
      <div className="bg-base-100 shadow rounded-lg p-6 border border-accent">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-base-content opacity-900">Recent Clinical Notes</h3>
          <button className="text-sm text-secondary hover:text-secondary-focus">
            View All
          </button>
        </div>

        <div className="space-y-4">
          {loading ? (
            <p className="text-base-content opacity-500 text-sm">Loading...</p>
          ) : summary.recent_notes.length > 0 ? (
            summary.recent_notes.map((note) => (
              <div key={note.id} className="border-b pb-3">
                <div className="flex justify-between">
                  <p className="font-medium">{note.subject || note.note_type || 'Clinical Note'}</p>
                  <p className="text-sm text-base-content opacity-500">{formatDate(note.note_date)}</p>
                </div>
                <p className="text-sm text-base-content opacity-600 line-clamp-2">
                  {note.content || 'No content available'}
                </p>
                <p className="text-xs text-base-content opacity-500 mt-1">
                  By: {note.provider_name || 'Unknown'}
                </p>
              </div>
            ))
          ) : (
            <p className="text-base-content opacity-500 text-sm">No clinical notes available</p>
          )}
        </div>
      </div>

      {/* Medications */}
      <div className="bg-base-100 shadow rounded-lg p-6 border border-accent">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-medium text-base-content opacity-900">Medications</h3>
          <button
            onClick={() => { setDiscontinuingMedication(null); setIsMedicationModalOpen(true); }}
            className="text-sm text-secondary hover:text-secondary-focus"
          >
            Add
          </button>
        </div>
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            className="checkbox checkbox-xs"
            checked={showDiscontinuedMeds}
            onChange={(e) => setShowDiscontinuedMeds(e.target.checked)}
          />
          <span className="text-xs text-base-content opacity-70">Show discontinued</span>
        </label>

        <div className="space-y-3">
          {loading ? (
            <p className="text-base-content opacity-500 text-sm">Loading...</p>
          ) : activeMedications.length > 0 || discontinuedMedications.length > 0 ? (
            <>
              {activeMedications.map((medication) => (
                <div key={medication.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{medication.name}</p>
                      <p className="text-sm">{medication.dosage} {medication.frequency}</p>
                      <p className="text-xs text-base-content opacity-500">
                        {medication.start_date && `Started: ${formatDate(medication.start_date)}`}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        Active
                      </span>
                      <button
                        onClick={() => { setDiscontinuingMedication(medication); setIsMedicationModalOpen(true); }}
                        className="text-xs text-secondary hover:text-secondary-focus"
                      >
                        Discontinue
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {showDiscontinuedMeds && discontinuedMedications.map((medication) => (
                <div key={medication.id} className="p-3 bg-gray-50 rounded-lg opacity-70">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{medication.name}</p>
                      <p className="text-sm">{medication.dosage} {medication.frequency}</p>
                      <p className="text-xs text-base-content opacity-500">
                        {medication.start_date && `Started: ${formatDate(medication.start_date)}`}
                        {medication.end_date && ` | Discontinued: ${formatDate(medication.end_date)}`}
                      </p>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium bg-base-300 text-base-content rounded-full">
                      Discontinued
                    </span>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <p className="text-base-content opacity-500 text-sm">No medications recorded</p>
          )}
        </div>
      </div>

      {/* Insurance Summary */}
      <div className="col-span-full lg:col-span-1 bg-base-100 shadow rounded-lg p-6 border border-accent">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-base-content opacity-900">Insurance</h3>
          <button className="text-sm text-secondary hover:text-secondary-focus">
            Details
          </button>
        </div>

        <div className="space-y-4">
          {patient.insurance && patient.insurance.length > 0 ? (
            <>
              {/* Primary insurance display */}
              {patient.insurance.find(ins => ins.is_primary) && (
                <div className="border-b pb-4">
                  <p className="text-sm font-medium text-base-content opacity-500">Primary Insurance</p>
                  <div className="mt-2">
                    <p className="font-medium">
                      {patient.insurance.find(ins => ins.is_primary)?.provider_name || 'Unknown Provider'}
                    </p>
                    <p className="text-sm">
                      Policy: {patient.insurance.find(ins => ins.is_primary)?.policy_number || 'N/A'}
                    </p>
                    <p className="text-xs text-base-content opacity-500">
                      {patient.insurance.find(ins => ins.is_primary)?.insurance_type_display ||
                       patient.insurance.find(ins => ins.is_primary)?.insurance_type || 'N/A'}
                    </p>
                  </div>
                </div>
              )}

              {/* Show other insurances */}
              <div>
                <p className="text-sm font-medium text-base-content opacity-500 mb-2">Other Coverage</p>
                {patient.insurance
                  .filter(ins => !ins.is_primary)
                  .map((insurance, index) => (
                    <div key={insurance.id || index} className="flex items-center justify-between mb-2">
                      <span>{insurance.provider_name || insurance.insurance_type_display || insurance.insurance_type}</span>
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">Active</span>
                    </div>
                  ))
                }
              </div>
            </>
          ) : (
            <p className="text-base-content opacity-500 text-sm">No insurance on file</p>
          )}

          {/* Medicare display */}
          {patient.medicare && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium text-base-content opacity-500">Medicare</p>
              <p className="font-medium mt-1">
                {patient.medicare.medicare_number}-{patient.medicare.reference_number}
              </p>
              <p className="text-xs text-base-content opacity-500">
                Expires: {formatDate(patient.medicare.expiry_date)}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>

      {/* Add Diagnosis Modal */}
      <AddDiagnosisModal
        isOpen={isDiagnosisModalOpen}
        onClose={() => setIsDiagnosisModalOpen(false)}
        patientId={patient.id}
        patient={patient}
        onDiagnosisAdded={handleDiagnosisAdded}
      />

      {/* Add Allergy Modal */}
      <AddAllergyModal
        isOpen={isAllergyModalOpen}
        onClose={() => setIsAllergyModalOpen(false)}
        patientId={patient.id}
        patient={patient}
        onAllergyAdded={handleAllergyAdded}
      />

      {/* Add/Discontinue Medication Modal */}
      <AddMedicationModal
        isOpen={isMedicationModalOpen}
        onClose={() => { setIsMedicationModalOpen(false); setDiscontinuingMedication(null); }}
        patientId={patient.id}
        patient={patient}
        onMedicationAdded={handleMedicationAdded}
        editingMedication={discontinuingMedication}
      />
    </div>
  );
};

export default OverviewTab;
